import React, { useRef, useEffect } from 'react';
import { ReactComponent as ChatbotWindowCloseIcon } from "../../../../assets/ChatbotReportsSvgs/ChatbotWindowCloseIcon.svg";
import { ReactComponent as SendButtonIcon } from "../../../../assets/ChatbotReportsSvgs/SendButtonIconChatbot.svg";
import { ReactComponent as StopChatbotIcon } from "../../../../assets/ChatbotReportsSvgs/StopChatbotIcon.svg";
import { ReactComponent as VoiceInputChatbotIcon } from "../../../../assets/ChatbotReportsSvgs/VoiceInuputChatbotIcon.svg";
import { ReactComponent as FileUploadChatbotIcon } from "../../../../assets/ChatbotReportsSvgs/FileUploadChatbotIcon.svg";
// import {
//     ChatHistorySessionInterface,
//     ChatHistoryMessageInterface,
//     MetricsDataInterface,
//     InsightsDataInterface,
//     ConvMessage,
// } from 'interface/ReportsChatbotInterface';
import MetricsMessageBubble from '../MetricsMessageBubble';
import InsightsMessageBubble from '../InsightsMessageBubble';
import UserQueryMessageBubble from '../UserQueryMessageBubble';
import ChatbotResponseMessageBubble from '../ChatbotResponseMessageBubble';
import DetailMessageBubble from '../DetailMessageBubble';
import KpiCardBubble from '../KpiCardBubble';
import './style.scss';
import { ChatHistoryMessageInterface, ChatHistorySessionInterface, ConvMessage, InsightsDataInterface } from '../../../../interface/ReportsChatbotInterface';

interface ChatHistoryProps {
    session: ChatHistorySessionInterface;
    chatbotName?: string;
    liveMessages?: ConvMessage[];
    onSendMessage: (text?: string) => void;
    inputValue: string;
    setInputValue: (v: string) => void;
    isWaitingForResponse: boolean;
    iconColor: string;
    messageInputPlaceholder: string;
    footerText: string;
    enableVoiceInput?: boolean;
    enableFileUpload?: boolean;
    apiFailureResponse?: string;
    onStop: () => void;
    onBack: () => void;
    onClose: () => void;
}

const formatTime = (isoString: string): string => {
    try {
        return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

const formatDate = (isoString: string): string => {
    try {
        return new Date(isoString).toLocaleDateString([], { month: 'short', day: '2-digit', year: 'numeric' });
    } catch {
        return isoString;
    }
};

const getNowTimeLabel = (): string => {
    try {
        return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

// Renders a historical message from the session
const renderHistoryMessage = (
    msg: ChatHistoryMessageInterface,
    index: number,
    sessionId: string,
    chatbotName: string
) => {
    const timeLabel = formatTime(msg.timestamp);

    if (msg.role === 'assistant' && msg.type === 'greeting' && msg.metrics === null) {
        return <ChatbotResponseMessageBubble key={index} text={msg.content} />;
    }

    if (msg.role === 'user' && msg.type === null && msg.metrics === null) {
        return <UserQueryMessageBubble key={index} text={msg.content} />;
    }

    if (msg.role === 'assistant' && msg.type === null && (msg.card || msg.metrics !== null)) {
        const insightsData: InsightsDataInterface = {
            session_id: sessionId,
            insight: msg.content,
            currency: { code: '', symbol: '' },
        };
        return (
            <React.Fragment key={index}>
                {msg.card ? (
                    <KpiCardBubble sender="ai" card={msg.card} />
                ) : msg.metrics ? (
                    <MetricsMessageBubble
                        sender="ai"
                        metricsData={{
                            session_id: sessionId,
                            has_metrics: true,
                            has_data: true,
                            metrics: msg.metrics,
                            currency: { code: msg.metrics.currency ?? '', symbol: '' },
                        }}
                        isLoadingMetricsData={false}
                    />
                ) : null}
                <InsightsMessageBubble
                    sender="ai"
                    chatbotName={chatbotName}
                    insightsData={insightsData}
                    isLoadingInsightsData={false}
                    timeLabel={timeLabel}
                    onThumbsUp={() => {}}
                    onThumbsDown={() => {}}
                />
            </React.Fragment>
        );
    }

    if (msg.role === 'assistant' && msg.type === null && msg.metrics === null) {
        const insightsData: InsightsDataInterface = {
            session_id: sessionId,
            insight: msg.content,
            currency: { code: '', symbol: '' },
        };
        return (
            <InsightsMessageBubble
                key={index}
                sender="ai"
                chatbotName={chatbotName}
                insightsData={insightsData}
                isLoadingInsightsData={false}
                timeLabel={timeLabel}
                onThumbsUp={() => {}}
                onThumbsDown={() => {}}
            />
        );
    }

    return null;
};

// Renders a live ConvMessage from the Redux conversation state
const renderLiveMessage = (
    msg: ConvMessage,
    chatbotName: string,
    apiFailureResponse: string
) => {
    if (msg.type === 'welcome' || msg.type === 'ai-reply') {
        return <ChatbotResponseMessageBubble key={msg.id} text={msg.text} />;
    }
    if (msg.type === 'user') {
        return <UserQueryMessageBubble key={msg.id} text={msg.text} />;
    }
    if (msg.type === 'ai-data') {
        if (msg.status === 'skip' && !msg.errorMessage) return null;
        if (msg.status === 'skip' && msg.errorMessage) {
            return <DetailMessageBubble key={msg.id} sender="ai" isFailure={true} apiFailureMessage={msg.errorMessage} />;
        }
        if ((msg.status === 'metrics' || msg.status === 'detail') && msg.data?.card) {
            return (
                <KpiCardBubble
                    key={msg.id}
                    sender="ai"
                    card={msg.data.card}
                    isFailure={!!msg.errorMessage}
                    apiFailureMessage={msg.errorMessage ?? apiFailureResponse}
                />
            );
        }
        if (msg.status === 'metrics') {
            return (
                <MetricsMessageBubble
                    key={msg.id}
                    sender="ai"
                    isLoadingMetricsData={false}
                    metricsData={msg.data}
                    isFailureMetricsData={!!msg.errorMessage}
                    apiFailureResponeMetricsData={msg.errorMessage ?? apiFailureResponse}
                />
            );
        }
        if (msg.status === 'detail') {
            return (
                <DetailMessageBubble
                    key={msg.id}
                    sender="ai"
                    rawResults={msg.data?.raw_results}
                    isLoading={false}
                    isFailure={!!msg.errorMessage}
                    apiFailureMessage={msg.errorMessage ?? apiFailureResponse}
                />
            );
        }
        return <DetailMessageBubble key={msg.id} sender="ai" isLoading={msg.status === 'loading'} />;
    }
    if (msg.type === 'ai-insights') {
        return (
            <InsightsMessageBubble
                key={msg.id}
                sender="ai"
                chatbotName={chatbotName}
                insightsData={msg.status === 'success' && msg.data ? msg.data : { session_id: '', insight: '', currency: { code: '', symbol: '' } }}
                isLoadingInsightsData={msg.status === 'loading'}
                isFailureInsightsData={msg.status === 'failure'}
                apiFailureResponeInsightsData={msg.errorMessage ?? apiFailureResponse}
                timeLabel={getNowTimeLabel()}
                onThumbsUp={() => {}}
                onThumbsDown={() => {}}
            />
        );
    }
    return null;
};

const ChatHistory: React.FC<ChatHistoryProps> = ({
    session,
    chatbotName = 'Maghil Bot',
    liveMessages = [],
    onSendMessage,
    inputValue,
    setInputValue,
    isWaitingForResponse,
    iconColor,
    messageInputPlaceholder,
    footerText,
    enableVoiceInput = true,
    enableFileUpload = true,
    apiFailureResponse = 'Unable to retrieve at the moment',
    onStop,
    onBack,
    onClose,
}) => {
    const scrollBottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        scrollBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [liveMessages.length]);

    useEffect(() => {
        scrollBottomRef.current?.scrollIntoView({ behavior: 'auto' });
    }, [session.session_id]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isWaitingForResponse) onSendMessage();
    };

    return (
        <div className="chat-history-container">
            {/* <div className="chat-history-header">
                <button className="chat-history-back-btn" type="button" onClick={onBack} aria-label="Back">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <span className="chat-history-title">Recent Insights</span>
                <ChatbotWindowCloseIcon className="chat-history-close-icon" onClick={onClose} />
            </div> */}

            <div className="chat-history-scroll">
                <div className="chat-history-messages">
                    <div className="chat-history-date-separator">
                        {formatDate(session.created_at)}
                    </div>

                    {session.messages.map((msg, index) =>
                        renderHistoryMessage(msg, index, session.session_id, chatbotName)
                    )}

                    {liveMessages.length > 0 && (
                        <>
                            <div className="chat-history-date-separator">Now</div>
                            {liveMessages.map(msg => renderLiveMessage(msg, chatbotName, apiFailureResponse))}
                        </>
                    )}

                    <div ref={scrollBottomRef} />
                </div>
            </div>

            <div className="chat-history-input-area">
                <div className="message-input-box-container-reports-chatbot">
                    {enableFileUpload && <FileUploadChatbotIcon />}
                    <input
                        type="text"
                        placeholder={messageInputPlaceholder}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    {enableVoiceInput && <VoiceInputChatbotIcon />}
                    {isWaitingForResponse ? (
                        <StopChatbotIcon
                            className="stop-btn-icon"
                            onClick={onStop}
                        />
                    ) : (
                        <SendButtonIcon
                            className="send-btn-icon"
                            onClick={() => onSendMessage()}
                            style={{ color: iconColor }}
                        />
                    )}
                </div>
                <div className="footer-chatbot-container">{footerText}</div>
            </div>
        </div>
    );
};

export default ChatHistory;
